import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import type { ApiError } from '@/core/api';
import { MemberSelect } from '@/features/members';
import { fromLocalInput, toLocalInput } from '../datetime';
import type { CalendarEvent, CalendarEventInput } from '../types';

const schema = z
  .object({
    title: z.string().trim().min(1, 'Title is required').max(255),
    starts_at: z.string().min(1, 'Start is required'),
    ends_at: z.string(),
    all_day: z.boolean(),
    kind: z.string().max(50),
    owner: z.string(),
  })
  .refine((v) => !v.ends_at || v.ends_at >= v.starts_at, {
    message: 'End must be on or after the start.',
    path: ['ends_at'],
  });

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['title', 'starts_at', 'ends_at', 'all_day', 'kind', 'owner'] as const;

interface EventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: CalendarEvent;
  onSubmit: (input: CalendarEventInput) => Promise<unknown>;
}

function toValues(event?: CalendarEvent): FormValues {
  return {
    title: event?.title ?? '',
    starts_at: toLocalInput(event?.starts_at ?? null),
    ends_at: toLocalInput(event?.ends_at ?? null),
    all_day: event?.all_day ?? false,
    kind: event?.kind ?? '',
    owner: event?.owner ? String(event.owner) : '',
  };
}

export function EventFormDialog({ open, onOpenChange, event, onSubmit }: EventFormDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toValues(event) });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(event));
  }, [open, event, reset]);

  async function handleSubmit(values: FormValues) {
    const startsIso = fromLocalInput(values.starts_at);
    if (!startsIso) {
      form.setError('starts_at', { message: 'Enter a valid date and time.' });
      return;
    }
    const input: CalendarEventInput = {
      title: values.title,
      starts_at: startsIso,
      ends_at: fromLocalInput(values.ends_at),
      all_day: values.all_day,
      kind: values.kind.trim(),
      owner: values.owner ? Number(values.owner) : null,
    };
    try {
      await onSubmit(input);
      onOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{event ? 'Edit event' : 'New event'}</DialogTitle>
          <DialogDescription>
            {event ? 'Update the event details.' : 'Add an event to the calendar.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Event" description="What is happening?">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Client kickoff" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="When" description="Schedule and duration.">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="starts_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Starts</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ends_at"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1.5">
                        Ends
                        <InfoHint text="Optional." />
                      </FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="all_day"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <FormLabel className="m-0">All day</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Kind & owner" description="Classification and assignment.">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="kind"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kind</FormLabel>
                      <FormControl>
                        <Input placeholder="meeting, milestone…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="owner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Owner</FormLabel>
                      <FormControl>
                        <MemberSelect
                          queryKey="event-owner-picker"
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Unassigned"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : event ? 'Save changes' : 'Create event'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
